/**
 * Foutmeldingen naar Digital Guardian (https://guardian.digitalkiss.nl).
 *
 * Contract: docs/GUARDIAN-INTEGRATIE.md in de bombyxx-map. Dezelfde opzet als de
 * CMS-client (cms/src/functions/api/guardian.js); verschillen staan hieronder.
 *
 * DE GOUDEN REGEL: alles wat per voorval verschilt hoort in `context`, nooit in
 * `title`. Guardian groepeert op de titeltekst, dus een ID, pagina of foutmelding
 * in de titel maakt van elk voorval een eigen issue en loopt het kanaal vol. De
 * pagina hoort in `context.url` -- daar telt Guardian unieke waarden van.
 *
 * Severity bepaalt waar het landt: critical = urgent kanaal met @here, error =
 * issues-kanaal, warning/info = alleen de digest.
 */
const ENDPOINT = "https://guardian.digitalkiss.nl/api/events";

/**
 * Eén slug voor webshop én API: dat is één applicatie. Het onderscheid maak je met
 * `type`. Vroeger stond de omgeving in de naam ("Bombyxx DEV"); dat maakte er twee
 * projecten van in plaats van één project met twee omgevingen.
 */
const PROJECT = "bombyxx";

/**
 * Een schuivend venster in plaats van een vaste teller per pageload: een klant die
 * een uur door de shop klikt zou anders na tien meldingen niets meer melden.
 */
const MAX_PER_VENSTER = 10;
const VENSTER_MS = 5 * 60 * 1000;
const HERHAAL_COOLDOWN_MS = 10000;

/** Lokaal meldt niets: `npm start` laadt .env.development, dus een env-check volstaat niet. */
const isLokaal = () => {
  const h = typeof window !== "undefined" ? window.location.hostname : "";
  return h === "localhost" || h === "127.0.0.1" || h === "[::1]" || h.endsWith(".localhost");
};

const ENVIRONMENT = process.env.REACT_APP_ENVIRONMENT === "production" ? "production" : "staging";
const RELEASE = process.env.REACT_APP_RELEASE || "unknown";

/** Fouten waar je toch niets mee kunt: extensies, meetruis, cross-origin. */
const NEGEER = [
  /^Script error\.?$/,
  /ResizeObserver loop/,
  /chrome-extension:\/\//,
  /moz-extension:\/\//,
  /safari-extension:\/\//,
];

/** Crawlers draaien de shop half en gooien fouten die geen klant ooit ziet. */
const BOT = /bot|crawl|spider|slurp|bingpreview|headless|lighthouse/i;

export const moetGenegeerdWorden = (tekst) => !!tekst && NEGEER.some((r) => r.test(tekst));

export const isPermissieFout = (fout) => {
  const tekst = (fout && ((fout.reason && fout.reason.message) || fout.message)) || "";
  return (
    tekst.includes("Missing or insufficient permissions") ||
    tekst.includes("PERMISSION_DENIED") ||
    tekst.includes("insufficient permissions") ||
    (fout && fout.code === "permission-denied")
  );
};

let recenteMeldingen = [];
const laatsteMelding = new Map();
let gedeeldeContext = {};

const binnenBudget = () => {
  const grens = Date.now() - VENSTER_MS;
  recenteMeldingen = recenteMeldingen.filter((t) => t > grens);
  return recenteMeldingen.length < MAX_PER_VENSTER;
};

/**
 * Context die bij elke melding mee moet maar die deze module niet zelf weet:
 * of er iemand is ingelogd (en wie, als uid -- geen naam of e-mail).
 * Wordt bijgewerkt vanuit de auth-listener in App.js.
 */
export const setGuardianContext = (extra) => {
  gedeeldeContext = { ...gedeeldeContext, ...extra };
};

/**
 * Een permissiefout zonder ingelogde gebruiker is vrijwel altijd een listener die nog
 * even doorliep tijdens het uitloggen (zie de cache-reset in App.js). Die gaat naar de
 * digest; mét ingelogde gebruiker is het een echte fout in de rechten.
 */
export const permissieSeverity = () => (gedeeldeContext.ingelogd ? "error" : "warning");

export const report = (event = {}) => {
  try {
    if (isLokaal()) return;
    if (typeof navigator !== "undefined" && BOT.test(navigator.userAgent || "")) return;
    if (!binnenBudget()) return;

    const sleutel = `${event.type || "unknown"}|${event.title || ""}`;
    const nu = Date.now();
    if (laatsteMelding.has(sleutel) && nu - laatsteMelding.get(sleutel) < HERHAAL_COOLDOWN_MS) return;
    laatsteMelding.set(sleutel, nu);
    recenteMeldingen.push(nu);

    fetch(ENDPOINT, {
      method: "POST",
      keepalive: true, // overleeft het verlaten van de pagina
      headers: { "content-type": "application/json" },
      body: JSON.stringify({
        project: PROJECT,
        environment: ENVIRONMENT,
        release: RELEASE,
        severity: "error",
        type: "client_crash",
        ...event,
        context: {
          url: window.location.pathname,
          userAgent: navigator.userAgent,
          ...gedeeldeContext,
          ...event.context,
        },
      }),
    }).catch(() => {
      // Melden mag nooit zelf iets breken.
    });
  } catch (e) {
    // Idem.
  }
};

/** Stack inkorten tot wat Guardian per contextwaarde bewaart. */
export const stackVan = (fout) => (fout && fout.stack ? String(fout.stack).slice(0, 2000) : undefined);

if (typeof window !== "undefined") {
  window.addEventListener("error", (e) => {
    const message = e.message || "";
    if (moetGenegeerdWorden(message) || moetGenegeerdWorden(e.filename || "")) return;

    // Oude bundle na een deploy: één keer herladen lost het op voor de klant.
    if (/Loading chunk \d+ failed|Loading CSS chunk|dynamically imported module/.test(message)) {
      try {
        if (!sessionStorage.getItem("guardian:reloaded")) {
          sessionStorage.setItem("guardian:reloaded", "1");
          window.location.reload();
          return;
        }
      } catch (err) {
        // Storage geblokkeerd: dan gewoon melden.
      }
      report({ severity: "info", type: "stale_bundle", title: "Chunk laden mislukt na reload" });
      return;
    }

    if (isPermissieFout(e.error || e)) {
      report({ severity: permissieSeverity(), type: "firestore_read_failed", title: "Firestore-permissie geweigerd" });
      return;
    }

    report({ title: message, context: { stack: stackVan(e.error) } });
  });

  window.addEventListener("unhandledrejection", (e) => {
    const reason = (e.reason && e.reason.message) || String(e.reason || "");
    if (moetGenegeerdWorden(reason)) return;

    if (isPermissieFout(e.reason)) {
      report({ severity: permissieSeverity(), type: "firestore_read_failed", title: "Firestore-permissie geweigerd" });
      return;
    }

    report({ type: "unhandled_rejection", title: reason, context: { stack: stackVan(e.reason) } });
  });
}

export default report;
